import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
} from "@material-ui/core";
import { Collections, Protest } from "api";
import { Form, Formik } from "formik";
import { useSnackbar } from "notistack";
import React, { useContext, useMemo } from "react";
import {
    FormButton,
    InputFieldFormik,
    LoggedUserContext,
    SubmitButton,
} from "ui/componentes";
import { DefenseValidation } from "ui/views";
import { sendDefense } from "ui/views/Protests";
import { useIsLimitToProtestDefenseExcedeed } from "ui/views/Protests/hooks/useIsLimitToProtestDefenseExcedeed";
import { useFirestore } from "react-redux-firebase";

interface ProtestInfoDialogProps {
    show: boolean;
    protest?: Protest;
    onClose: () => void;
    driversByNumber: object;
    canUpdateRaceControlAnswer?: boolean;
}

const ProtestInfoDialog: React.FC<ProtestInfoDialogProps> = ({
    show,
    onClose,
    protest,
    driversByNumber,
    canUpdateRaceControlAnswer = false,
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const firestore = useFirestore();
    const loggedUser = useContext(LoggedUserContext);
    const dateLimitToDefenseExcedeed = useIsLimitToProtestDefenseExcedeed(
        protest
    );
    const canNotIncludeDefense = useMemo(() => {
        if (!protest) return true;

        return (
            dateLimitToDefenseExcedeed ||
            !!protest?.defense ||
            protest?.authorUserId === loggedUser.id
        );

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dateLimitToDefenseExcedeed, protest]);

    return (
        <Formik
            enableReinitialize={true}
            initialValues={{
                defense: protest?.defense || "",
                protestId: protest?.id,
                raceControlAnswer: protest?.raceControlAnswer || "",
            }}
            validationSchema={DefenseValidation}
            onSubmit={(values, { setSubmitting }) => {
                sendDefense(values, setSubmitting, enqueueSnackbar).then(() => {
                    if (onClose) {
                        onClose();
                    }
                });
            }}
        >
            {({ values, isSubmitting }) => {
                return (
                    <Dialog open={show} maxWidth="md" onClose={onClose}>
                        <Form>
                            <DialogTitle>Protesto</DialogTitle>
                            <DialogContent>
                                <TextField
                                    value={
                                        driversByNumber[
                                            protest?.authorDriverNumber || ""
                                        ]
                                    }
                                    label="Piloto protestante"
                                    disabled={true}
                                />
                                <TextField
                                    value={protest?.complaint}
                                    label="Descrição do incidente"
                                    rows={6}
                                    multiline
                                    disabled={true}
                                />
                                <TextField
                                    value={
                                        driversByNumber[
                                            protest?.protestedDriverNumber || ""
                                        ]
                                    }
                                    label="Piloto protestado"
                                    disabled={true}
                                />
                                <InputFieldFormik
                                    required
                                    autoFocus
                                    disabled={canNotIncludeDefense}
                                    id="defense"
                                    name="defense"
                                    label="Defesa"
                                    multiline
                                    rows={6}
                                    helperText={`Max.: ${values.defense.length} / 300`}
                                />
                                <InputFieldFormik
                                    required
                                    autoFocus
                                    disabled={!canUpdateRaceControlAnswer}
                                    id="raceControlAnswer"
                                    name="raceControlAnswer"
                                    label="Análise da moderação"
                                    multiline
                                    rows={6}
                                />
                            </DialogContent>
                            <DialogActions>
                                <Button fullWidth={false} onClick={onClose}>
                                    Fechar
                                </Button>
                                {!canNotIncludeDefense && (
                                    <SubmitButton>
                                        {isSubmitting
                                            ? "Enviando defesa"
                                            : "Enviar defesa"}
                                    </SubmitButton>
                                )}
                                {canUpdateRaceControlAnswer && (
                                    <FormButton
                                        color="primary"
                                        disabled={!protest}
                                        onClick={() => {
                                            if (!protest) return;

                                            const {
                                                id,
                                                ...protestToUpdate
                                            } = protest;
                                            firestore
                                                .collection(
                                                    Collections.PROTESTS
                                                )
                                                .doc(id)
                                                .update({
                                                    ...protestToUpdate,
                                                    raceControlAnswer:
                                                        values.raceControlAnswer,
                                                })
                                                .then(() => {
                                                    enqueueSnackbar(
                                                        "Protesto atualizado",
                                                        { variant: "success" }
                                                    );
                                                    onClose();
                                                });
                                        }}
                                    >
                                        Salvar
                                    </FormButton>
                                )}
                            </DialogActions>
                        </Form>
                    </Dialog>
                );
            }}
        </Formik>
    );
};

export { ProtestInfoDialog };
